import type { PackPurchase } from "@/api";

export const TIER_ORDER = [
  "common",
  "uncommon",
  "rare",
  "epic",
  "legendary",
] as const;

export type RipTier = (typeof TIER_ORDER)[number];

export type RipBeat = {
  actualTier: RipTier;
  shownTier: RipTier;
  upgradeGap: number;
  rainbow: boolean;
  fakeFlipSchedule: boolean[];
  teaseCount: number;
  sweatLevel: number;
};

export type ResolvedPresentation = {
  status: "resolved" | "fallback";
  order: number[];
  hitIndex: number;
  beats: RipBeat[];
};

export function isRipTier(value: unknown): value is RipTier {
  return (
    typeof value === "string" &&
    (TIER_ORDER as readonly string[]).includes(value)
  );
}

export function canUpgrade(shown: RipTier, actual: RipTier) {
  return TIER_ORDER.indexOf(shown) < TIER_ORDER.indexOf(actual);
}

function fallback(purchase: PackPurchase): ResolvedPresentation {
  const hitIndex = purchase.contents.reduce(
    (best, horse, index, horses) =>
      horse.stickerCents > horses[best].stickerCents ? index : best,
    0,
  );
  return {
    status: "fallback",
    order: purchase.contents.map((_, index) => index),
    hitIndex,
    beats: purchase.contents.map((horse) => {
      const tier = isRipTier(horse.tier) ? horse.tier : "common";
      return {
        actualTier: tier,
        shownTier: tier,
        upgradeGap: 0,
        rainbow: false,
        fakeFlipSchedule: [],
        teaseCount: 0,
        sweatLevel: 0,
      };
    }),
  };
}

export function resolvePresentation(
  purchase: PackPurchase,
  reducedMotion: boolean,
): ResolvedPresentation {
  const plan = purchase.presentationPlan;
  const base = fallback(purchase);
  if (
    !plan ||
    plan.v !== 1 ||
    plan.order.length !== purchase.contents.length ||
    new Set(plan.order).size !== purchase.contents.length ||
    plan.order.some(
      (index) =>
        !Number.isInteger(index) ||
        index < 0 ||
        index >= purchase.contents.length,
    ) ||
    !plan.beats
  ) {
    return base;
  }
  const hitIndex =
    plan.featuredIndex ??
    purchase.contents.findIndex((horse) => horse.horseId === plan.hitHorseId);
  if (hitIndex < 0 || hitIndex >= purchase.contents.length) return base;

  let rainbowCount = 0;
  const beats: RipBeat[] = [];
  for (let index = 0; index < purchase.contents.length; index += 1) {
    const horse = purchase.contents[index];
    const beat = plan.beats[horse.horseId];
    if (!beat || !isRipTier(beat.actualTier) || !isRipTier(beat.shownTier)) {
      return base;
    }
    const gap =
      TIER_ORDER.indexOf(beat.actualTier) - TIER_ORDER.indexOf(beat.shownTier);
    if (
      gap < 0 ||
      beat.upgradeGap !== gap ||
      beat.fakeFlipSchedule.length !== gap ||
      beat.teaseCount !== beat.fakeFlipSchedule.filter(Boolean).length ||
      !Number.isFinite(beat.sweatLevel) ||
      beat.sweatLevel < 0 ||
      beat.sweatLevel > 1 ||
      (beat.rainbow &&
        (index !== hitIndex ||
          TIER_ORDER.indexOf(beat.actualTier) < TIER_ORDER.indexOf("rare") ||
          gap !== 0))
    ) {
      return base;
    }
    if (beat.rainbow) rainbowCount += 1;
    beats.push({
      actualTier: beat.actualTier,
      shownTier: reducedMotion ? beat.actualTier : beat.shownTier,
      upgradeGap: reducedMotion ? 0 : beat.upgradeGap,
      rainbow: reducedMotion ? false : beat.rainbow,
      fakeFlipSchedule: reducedMotion ? [] : [...beat.fakeFlipSchedule],
      teaseCount: reducedMotion ? 0 : beat.teaseCount,
      sweatLevel: reducedMotion ? 0 : beat.sweatLevel,
    });
  }
  if (rainbowCount > 1) return base;
  return {
    status: "resolved",
    order: reducedMotion
      ? purchase.contents.map((_, index) => index)
      : [...plan.order],
    hitIndex,
    beats,
  };
}
